import { useState, useRef } from "react";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import Input from "../../../Components/Common/Input/Input";
import DateSelect from "Components/Common/DateSelect/DateSelect";
import OwnerSignDrawRequestSection from "./OwnerSignDrawRequestSection";
import { uploadFileToS3 } from "../../GcPrecon/GcPreconCommercial/UploadFileToS3";
import { ownerSignDraw } from "../../../services/drawService";
import { useToast } from "Components/Common/Toast/ToastContext";
import SignaturePad, {
  SignaturePadHandle,
} from "Components/Common/SignaturePad/SignaturePad";
import Button from "Components/Common/Button/Button";

interface MoneyField {
  amount: number;
  currency: { symbol: string };
}

interface PendingDraw {
  id: string;
  drawNumber: number;
  toDate: string;
  project: { name: string };
  gc: { firstName: string; lastName: string; companyName: string };
  bank: { companyName: string; branchName: string };
  g702: {
    originalContractSum: MoneyField;
    sovUpdate: MoneyField;
    netChangeByChangeOrders: MoneyField;
    contractSumToDate: MoneyField;
    totalCompletedAndStored: MoneyField;
    retainageRate: string;
    totalRetainage: MoneyField;
    totalEarnedLessRetainage: MoneyField;
    lessPreviousCertificates: MoneyField;
    currentPaymentDue: MoneyField;
    balanceToFinish: MoneyField;
  };
}

const fmt = (f?: MoneyField) =>
  f
    ? `${f.currency.symbol}${f.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";

interface Props {
  projectId: string;
  pendingDraw: PendingDraw | null;
  aiaSigned: boolean;
  onSigned: () => void;
  loading?: boolean;
}

export default function OwnerSignAIASection({
  projectId,
  pendingDraw: draw,
  aiaSigned,
  onSigned,
  loading,
}: Props) {
  const [hasSig, setHasSig] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signError, setSignError] = useState(false);
  const padRef = useRef<SignaturePadHandle>(null);

  const ownerDetail = JSON.parse(localStorage.getItem("user") || "{}");
  const showToast = useToast();
  const [ownerName, setOwnerName] = useState(
    `${ownerDetail?.firstName ?? ""} ${ownerDetail?.lastName ?? ""}`.trim(),
  );
  const [ownerTitle, setOwnerTitle] = useState(ownerDetail?.designation ?? "");
  const [ownerDate, setOwnerDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const ownerCompany: string = ownerDetail?.companyName ?? "";

  const submitAIA = async () => {
    if (!hasSig) {
      setSignError(true);
      document
        .getElementById("ownerSignaturePad")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSignError(false);
    const activeDrawId = draw?.id;
    if (!activeDrawId) {
      showToast("No pending draw found");
      return;
    }
    if (!ownerName.trim()) {
      showToast("Owner name is required");
      return;
    }

    setSubmitting(true);
    try {
      let s3Key = "";
      if (padRef.current?.isSigned()) {
        const blob = await padRef.current.getBlob();
        const file = new File([blob], "owner-signature.png", {
          type: "image/png",
        });
        const result: any = await uploadFileToS3(file);
        s3Key = result?.key ?? result?.Key ?? "";
      }

      const { error } = (await (ownerSignDraw(projectId, activeDrawId, {
        ownerName,
        ownerSignatureUrl: { key: s3Key, fileName: "signature.png" },
        ownerDesignation: ownerTitle,
        ownerCompany,
      }) as Promise<{ data: unknown; error: unknown }>)) as {
        data: unknown;
        error: unknown;
      };

      if (error) {
        showToast("Failed to submit signature. Please try again.");
        return;
      }

      onSigned();
      showToast(
        `✓ Draw #${draw?.drawNumber ?? ""} signed — ${draw?.bank?.companyName ?? "Bank"} and CSC notified`,
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      showToast("Failed to submit signature. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <OwnerSignDrawRequestSection loading />
        <div className="card">
          <Skeleton height={18} width="40%" className="aiaSkelTitle" />
          <div className="aiaSkelGrid2">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="aiaSkelRow">
                <Skeleton width="55%" height={13} />
                <Skeleton width="30%" height={13} />
              </div>
            ))}
          </div>
          <Skeleton height={1} className="aiaSkelDivider" />
          <div className="aiaSkelGrid2Sm">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} width="60%" height={13} />
            ))}
          </div>
        </div>
        <div className="card">
          <Skeleton height={18} width="35%" className="aiaSkelTitleSm" />
          <Skeleton height={60} className="aiaSkelRounded" />
          <div className="aiaSkelFieldsGrid">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height={36} className="aiaSkelField" />
            ))}
          </div>
          <Skeleton height={120} className="aiaSkelRounded" />
          <Skeleton height={44} className="aiaSkelBtn" />
        </div>
      </>
    );
  }

  if (!draw && !aiaSigned) {
    return (
      <>
        <OwnerSignDrawRequestSection />

        <div className="card aiaEmptyCard">
          <div className="aiaEmptyIcon">✅</div>
          <div className="aiaEmptyTitle">No Draws Pending Your Signature</div>
          <div className="aiaEmptySub">
            All draw requests have been signed. You'll be notified when a new
            draw requires your approval.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <OwnerSignDrawRequestSection
        bankName={draw?.bank?.companyName}
        isVisible={aiaSigned}
      />

      {aiaSigned && (
        <div className="success-banner">
          <div className="sb-icon">✅</div>
          <div className="sb-title">Draw #{draw?.drawNumber ?? ""} Signed</div>
          <div className="sb-sub">
            Your signature has been recorded.{" "}
            {draw?.bank?.branchName ?? draw?.bank?.companyName ?? "The bank"}{" "}
            and CSC New England have been notified. Draw #
            {draw?.drawNumber ?? ""} is now in process.
          </div>
        </div>
      )}

      {!aiaSigned && (
        <div id="aiaSignForm">
          <div className="card">
            <div className="card-title">
              AIA G702 — Application for Payment #{draw?.drawNumber ?? ""}
              <span className="read-only-badge">Read Only</span>
            </div>
            <div className="aia-summary">
              <div>
                <div className="aia-line">
                  <span>
                    <span className="aia-lnum">1</span>
                    <span className="aia-label">Original contract sum</span>
                  </span>
                  <span className="aia-val">
                    {fmt(draw?.g702?.originalContractSum)}
                  </span>
                </div>
                {/* <div className="aia-line">
                  <span>
                    <span className="aia-lnum">1a</span>
                    <span className="aia-label">SOV Update</span>
                  </span>
                  <span className="aia-val">{fmt(draw?.g702?.sovUpdate)}</span>
                </div> */}
                <div className="aia-line">
                  <span>
                    <span className="aia-lnum">2</span>
                    <span className="aia-label">
                      Net change by change orders
                    </span>
                  </span>
                  <span className="aia-val">
                    {fmt(draw?.g702?.netChangeByChangeOrders)}
                  </span>
                </div>
                <div className="aia-line">
                  <span>
                    <span className="aia-lnum">3</span>
                    <span className="aia-label">Contract sum to date</span>
                  </span>
                  <span className="aia-val">
                    {fmt(draw?.g702?.contractSumToDate)}
                  </span>
                </div>
                <div className="aia-line">
                  <span>
                    <span className="aia-lnum">4</span>
                    <span className="aia-label">
                      Total completed &amp; stored
                    </span>
                  </span>
                  <span className="aia-val">
                    {fmt(draw?.g702?.totalCompletedAndStored)}
                  </span>
                </div>
              </div>
              <div>
                <div className="aia-line">
                  <span>
                    <span className="aia-lnum">5</span>
                    <span className="aia-label">
                      Total retainage (
                      {draw?.g702?.retainageRate
                        ? `${parseFloat(draw.g702.retainageRate)}%`
                        : "10%"}
                      )
                    </span>
                  </span>
                  <span className="aia-val">
                    {fmt(draw?.g702?.totalRetainage)}
                  </span>
                </div>
                <div className="aia-line">
                  <span>
                    <span className="aia-lnum">6</span>
                    <span className="aia-label">
                      Total earned less retainage
                    </span>
                  </span>
                  <span className="aia-val blue">
                    {fmt(draw?.g702?.totalEarnedLessRetainage)}
                  </span>
                </div>
                <div className="aia-line">
                  <span>
                    <span className="aia-lnum">7</span>
                    <span className="aia-label">
                      Less previous certificates
                    </span>
                  </span>
                  <span className="aia-val">
                    {fmt(draw?.g702?.lessPreviousCertificates)}
                  </span>
                </div>
                <div className="aia-line total">
                  <span>
                    <span className="aia-lnum">8</span>
                    <span className="aia-label">Current payment due</span>
                  </span>
                  <span className="aia-val green">
                    {fmt(draw?.g702?.currentPaymentDue)}
                  </span>
                </div>
                <div className="aia-line">
                  <span>
                    <span className="aia-lnum">9</span>
                    <span className="aia-label">
                      Balance to finish incl. retainage
                    </span>
                  </span>
                  <span className="aia-val">
                    {fmt(draw?.g702?.balanceToFinish)}
                  </span>
                </div>
              </div>
            </div>
            <div className="aiaMetaGrid">
              <div>
                <span className="aiaMetaLabel">Contractor : </span>
                <strong>{draw?.gc?.companyName ?? "—"}</strong>
              </div>
              <div>
                <span className="aiaMetaLabel">Period to : </span>
                <strong>
                  {draw?.toDate
                    ? new Date(draw.toDate).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : "—"}
                </strong>
              </div>
              <div>
                <span className="aiaMetaLabel">Project : </span>
                <strong>{draw?.project?.name ?? "—"}</strong>
              </div>
              <div>
                <span className="aiaMetaLabel">Bank : </span>
                <strong>
                  {draw?.bank?.companyName ?? draw?.bank?.branchName ?? "—"}
                </strong>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Owner / Borrower Certification</div>
            <div className="cert-text">
              The undersigned Owner certifies that to the best of the Owner's
              knowledge, information, and belief, the Work covered by this
              Application for Payment has been completed in accordance with the
              Contract Documents, that all amounts have been previously paid by
              the Owner to the Contractor for Work for which previous
              Certificates for Payment were issued and payments received from
              the Owner, and that current payment shown herein is now due.
            </div>

            <div className="signDrawForm">
              <div className="aiaFieldCol">
                <label className="aiaFieldLabel">
                  Owner Name <span className="aiaReqMark">*</span>
                </label>
                <Input
                  type="text"
                  value={ownerName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setOwnerName(e.target.value)
                  }
                  className="aiaFieldInput"
                />
              </div>
              <div className="aiaFieldCol">
                <label className="aiaFieldLabel">Title</label>
                <Input
                  type="text"
                  value={ownerTitle}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setOwnerTitle(e.target.value)
                  }
                  className="aiaFieldInput"
                />
              </div>
              <div className="aiaFieldCol">
                <label className="aiaFieldLabel">Company</label>
                <Input
                  type="text"
                  value={ownerCompany}
                  readOnly
                  className="aiaFieldInput readOnly"
                />
              </div>
              <DateSelect
                label="Date"
                value={ownerDate}
                onChange={setOwnerDate}
              />
            </div>

            <div className="aiaSigLabel">
              Owner Signature <span className="aiaReqMark">*</span>
            </div>
            <div id="ownerSignaturePad">
              <SignaturePad
                ref={padRef}
                statusId="gcSignatureStatus"
                error={signError}
                onSignedChange={(signed) => {
                  setHasSig(signed);
                  if (signed) setSignError(false);
                }}
              />
            </div>
            <div className="aiaDisclaimer">
              By signing, you certify this application is accurate and authorize
              TD Bank to process this disbursement. CSC New England does not
              assume responsibility for the verification of signatures on this
              document.
            </div>

            <Button
              className="btn btn-sign"
              onClick={submitAIA}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div className="btn-spinner" />
                  Submitting
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M2 8l4 4 8-8"
                      stroke="#fff"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Sign & Submit Draw
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
